import { combineLatest, map, type Observable } from 'rxjs';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  toDataTourId,
  tourAnchorIds,
  type TourDefinition,
  type TourStep,
} from '@ardoq/product-tours';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { hasDescription } from 'presentation/slideNavigator/descriptionState';
import { slideNavigator$ } from 'presentation/slideNavigator/slideNavigator$';

export type PresentationNavigatorTourDependencies = {
  hasDescription: boolean;
  i18nLocale: I18nLocale;
};

const PLACEMENT_AND_INTERACTION = {
  placement: 'top',
  allowInteraction: 'all',
} as const;

const getPresentationNavigatorTourSteps = ({
  hasDescription,
  i18nLocale,
}: PresentationNavigatorTourDependencies): TourStep[] => [
  {
    ...PLACEMENT_AND_INTERACTION,
    id: 'slideOverview',
    target: toDataTourId(tourAnchorIds.presentationSlideList),
    title: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.slideOverviewTitle',
      'Jump to any slide'
    ),
    body: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.slideOverview',
      'Open the slide list and go straight to the one you want.'
    ),
  },
  // The description button only renders for a slide that has one, so the step
  // would have no anchor on the others.
  ...(hasDescription
    ? [
        {
          ...PLACEMENT_AND_INTERACTION,
          id: 'description',
          target: toDataTourId(tourAnchorIds.presentationSlideDescription),
          title: t(
            i18nLocale,
            'presentationAdmin:viewer.slideNavigator.tour.descriptionTitle',
            'Read the description'
          ),
          body: t(
            i18nLocale,
            'presentationAdmin:viewer.slideNavigator.tour.description',
            "Show this slide's description, or press D to toggle it."
          ),
        },
      ]
    : []),
  {
    ...PLACEMENT_AND_INTERACTION,
    id: 'slideNavigation',
    target: toDataTourId(tourAnchorIds.presentationSlideNavigation),
    title: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.slideNavigationTitle',
      'Move at your own pace'
    ),
    body: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.slideNavigation',
      'Step back and forward, or use the arrow keys.'
    ),
  },
  {
    ...PLACEMENT_AND_INTERACTION,
    id: 'hideNavigator',
    target: toDataTourId(tourAnchorIds.presentationHideNavigator),
    title: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.hideNavigatorTitle',
      'More room for the slide'
    ),
    body: t(
      i18nLocale,
      'presentationAdmin:viewer.slideNavigator.tour.hideNavigator',
      'Hide the bar for the full view. A button in the corner brings it back.'
    ),
  },
];

export const getPresentationNavigatorTourDefinition = (
  dependencies: PresentationNavigatorTourDependencies
): TourDefinition => ({
  id: 'presentation-navigator',
  dialogLabel: t(
    dependencies.i18nLocale,
    'presentationAdmin:viewer.slideNavigator.tour.dialogLabel',
    'Presentation controls'
  ),
  steps: getPresentationNavigatorTourSteps(dependencies),
});

export const presentationNavigatorTourDefinition$: Observable<TourDefinition> =
  combineLatest([slideNavigator$, i18nLocale$]).pipe(
    map(([state, i18nLocale]) =>
      getPresentationNavigatorTourDefinition({
        hasDescription: hasDescription(state),
        i18nLocale,
      })
    )
  );
